import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField,
  ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { fmtMoney, plural } from '../../../Documents/format';
import { CHART } from '../../constants';
import { deltaPct, fmtCompact, fmtDay, fmtDelta, fmtDuration, fmtInt, fmtPct, fmtStamp, monthLabel, share } from '../../format';
import { journalApi } from '../../journalApi';
import { useLoad } from '../../hooks/useLoad';
import { JournalFilters, PharmaciesResponse, PharmacyRank } from '../../types';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, ShareBar, Stat, StatStrip, SubText, TABLE_SX, TipBox, TipRow, TooltipInjected, clip, tooltipPoint
} from './chartKit';

interface Props {
  filters: JournalFilters;
  paramsKey: string;
  active: boolean;
  reloadToken: number;
  onPatch: (patch: Partial<JournalFilters>) => void;
}

type Metric = 'sum' | 'expiredSum' | 'expiredShare' | 'count';

const METRICS: Array<{ value: Metric; label: string; title: string }> = [
  { value: 'sum', label: 'Сумма', title: 'по сумме в журнале' },
  { value: 'expiredSum', label: 'Просрочено, ₸', title: 'по сумме просроченного товара' },
  { value: 'expiredShare', label: 'Просрочено, %', title: 'по доле просрочки в сумме аптеки' },
  { value: 'count', label: 'Записей', title: 'по числу записей' }
];

const TOP = 20;
const PAGE = 50;

type RankPoint = PharmacyRank & { label: string; rest: number };

const RankTip: React.FC<TooltipInjected<RankPoint> & { prevLabel?: string | null }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const d = deltaPct(p.sum, p.prevSum);
  const rows: TipRow[] = [
    { label: 'Сумма', value: fmtMoney(p.sum), bold: true, color: CHART.sum, hint: `${fmtPct(p.share)} суммы по фильтрам` },
    { label: 'Записей / товаров', value: `${fmtInt(p.count)} / ${fmtInt(p.products)}` },
    { label: 'Количество, уп.', value: fmtInt(p.qty) },
    { label: 'Просрочено', value: fmtMoney(p.expiredSum), color: CHART.expired, divider: true, hint: `${fmtPct(p.expiredShare)} суммы аптеки · ${fmtInt(p.expiredCount)} зап.` },
    { label: 'До 30 дней', value: fmtMoney(p.d30Sum), color: CHART.d30, hint: `${fmtInt(p.d30Count)} зап.` },
    { label: 'Ближайший срок', value: fmtDay(p.nearestExpiry) },
    { label: 'Средний остаток срока', value: p.avgDaysLeft === null ? '-' : fmtDuration(Math.round(p.avgDaysLeft)) }
  ];
  if (props.prevLabel) {
    rows.push({ label: props.prevLabel, value: p.prevSum === null ? '-' : fmtMoney(p.prevSum), color: CHART.prev, divider: true, hint: d === null ? 'сравнивать не с чем' : `сейчас ${fmtDelta(d)}` });
  }
  return (
    <TipBox
      width={300} title={p.name} subtitle={`${p.num}${p.region ? ` · ${p.region}` : ''}${p.active ? '' : ' · нет активной учётки'}`}
      rows={rows} footer="Клик - отфильтровать журнал по аптеке"
    />
  );
};

type SortKey = 'sum' | 'delta' | 'count' | 'products' | 'expiredSum' | 'expiredShare' | 'd30Sum' | 'nearestExpiry' | 'avgDaysLeft' | 'activeDays' | 'lastRecord' | 'num';

const sortValue = (p: PharmacyRank, key: SortKey): number | string | null => {
  if (key === 'delta') return deltaPct(p.sum, p.prevSum);
  if (key === 'num') return p.num;
  return p[key];
};

/** Сравнение с пустыми значениями в конце при любом направлении */
function compare(a: number | string | null, b: number | string | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), 'ru', { numeric: true }) * dir;
}

const COLUMNS: Array<{ key: SortKey; label: string; align?: 'right'; hint?: string; prevOnly?: boolean }> = [
  { key: 'num', label: 'Аптека' },
  { key: 'sum', label: 'Сумма', align: 'right' },
  { key: 'delta', label: 'К прошлому месяцу', align: 'right', prevOnly: true },
  { key: 'count', label: 'Записей', align: 'right' },
  { key: 'products', label: 'Товаров', align: 'right' },
  { key: 'expiredSum', label: 'Просрочено', align: 'right' },
  { key: 'expiredShare', label: '% просрочки', align: 'right' },
  { key: 'd30Sum', label: 'До 30 дней', align: 'right' },
  { key: 'nearestExpiry', label: 'Ближайший срок' },
  { key: 'avgDaysLeft', label: 'Ср. остаток срока', align: 'right' },
  { key: 'activeDays', label: 'Дней с вводом', align: 'right' },
  { key: 'lastRecord', label: 'Последняя запись' }
];

const ASC_FIRST: SortKey[] = ['num', 'nearestExpiry', 'avgDaysLeft'];

const RankChart: React.FC<{ data: PharmaciesResponse; onPick: (guid: string) => void }> = ({ data, onPick }) => {
  const [metric, setMetric] = useState<Metric>('sum');
  const prevLabel = data.previousMonth ? monthLabel(data.previousMonth) : null;
  const points = useMemo<RankPoint[]>(() => data.pharmacies
    .filter(p => metric === 'sum' || metric === 'count' || p.expiredSum > 0)
    .slice()
    .sort((a, b) => (b[metric] as number) - (a[metric] as number))
    .slice(0, TOP)
    .map(p => ({ ...p, label: `${p.num} ${p.name}`, rest: Math.max(0, p.sum - p.expiredSum - p.d30Sum) })),
  [data.pharmacies, metric]);
  const info = METRICS.find(m => m.value === metric) || METRICS[0];
  const choose = (item: { payload?: unknown }) => {
    const p = item.payload as RankPoint | undefined;
    if (p) onPick(p.guid);
  };
  const fmtAxis = (v: number) => (metric === 'expiredShare' ? `${fmtInt(v)}%` : metric === 'count' ? fmtInt(v) : fmtCompact(v));

  return (
    <ChartCard
      wide title={`Топ-${Math.min(TOP, points.length) || TOP} аптек ${info.title}`}
      subtitle={metric === 'sum'
        ? `Столбец - сумма аптеки с разбивкой по сроку${prevLabel ? `; серый - ${prevLabel.toLowerCase()}` : ''}. Клик - фильтр по аптеке.`
        : metric === 'count' ? 'Число записей в журнале по фильтрам. Клик - фильтр по аптеке.'
          : 'Только аптеки с просроченным товаром. Клик - фильтр по аптеке.'}
      actions={(
        <ToggleButtonGroup
          size="small" exclusive value={metric} onChange={(_, v: Metric | null) => { if (v) setMetric(v); }}
          sx={{ height: 28, '& .MuiToggleButton-root': { px: 1.1, py: 0, fontSize: '0.74rem', fontWeight: 600, textTransform: 'none' } }}
        >
          {METRICS.map(m => <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>)}
        </ToggleButtonGroup>
      )}
    >
      {!points.length ? <NoData height={200} text={metric === 'sum' || metric === 'count' ? undefined : 'Просроченного товара по фильтрам нет'} /> : (
        <Box sx={{ height: points.length * (metric === 'sum' && prevLabel ? 34 : 26) + 36 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap={4} barGap={1}>
              <CartesianGrid stroke={CHART.grid} horizontal={false} />
              <XAxis type="number" tick={AXIS_TICK} tickFormatter={v => fmtAxis(Number(v))} axisLine={{ stroke: BORDER }} tickLine={false} />
              <YAxis type="category" dataKey="label" width={200} tick={{ ...AXIS_TICK, fill: '#334155' }} axisLine={false} tickLine={false} interval={0} tickFormatter={v => clip(String(v), 30)} />
              <ChartTooltip content={<RankTip prevLabel={prevLabel} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
              {metric === 'sum' && <Bar dataKey="expiredSum" stackId="s" fill={CHART.expired} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'sum' && <Bar dataKey="d30Sum" stackId="s" fill={CHART.d30} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'sum' && <Bar dataKey="rest" stackId="s" fill="#93c5fd" radius={[0, 3, 3, 0]} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'sum' && prevLabel && <Bar dataKey="prevSum" fill="#cbd5e1" radius={[0, 3, 3, 0]} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'expiredSum' && <Bar dataKey="expiredSum" fill={CHART.expired} radius={[0, 3, 3, 0]} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'expiredShare' && <Bar dataKey="expiredShare" fill="#f87171" radius={[0, 3, 3, 0]} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
              {metric === 'count' && <Bar dataKey="count" fill={CHART.count} radius={[0, 3, 3, 0]} isAnimationActive={false} onClick={choose} style={{ cursor: 'pointer' }} />}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </ChartCard>
  );
};

const PharmaciesTable: React.FC<{ data: PharmaciesResponse; selected: string[]; onPick: (guid: string) => void }> = ({ data, selected, onPick }) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'sum', dir: -1 });
  const [shown, setShown] = useState(PAGE);
  const withPrev = Boolean(data.previousMonth);
  const columns = COLUMNS.filter(c => withPrev || !c.prevOnly);
  const maxShare = data.pharmacies.reduce((m, p) => Math.max(m, p.share), 0);

  const rank = useMemo(() => {
    const map: Record<string, number> = {};
    data.pharmacies.forEach((p, i) => { map[p.guid] = i + 1; });
    return map;
  }, [data.pharmacies]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? data.pharmacies.filter(p => `${p.num} ${p.name} ${p.region}`.toLowerCase().indexOf(q) !== -1)
      : data.pharmacies;
    return list.slice().sort((a, b) => compare(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir));
  }, [data.pharmacies, query, sort]);

  const toggleSort = (key: SortKey) => {
    setSort(s => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: ASC_FIRST.indexOf(key) !== -1 ? 1 : -1 }));
    setShown(PAGE);
  };

  return (
    <ChartCard
      wide title="Все аптеки"
      subtitle={`${fmtInt(data.pharmacies.length)} ${plural(data.pharmacies.length, 'аптека', 'аптеки', 'аптек')} с записями по фильтрам. Клик по строке - отфильтровать журнал по аптеке.`}
      actions={(
        <TextField
          size="small" placeholder="Номер, название, регион" value={query}
          onChange={e => { setQuery(e.target.value); setShown(PAGE); }}
          sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
        />
      )}
    >
      {!rows.length ? <NoData height={120} text={query ? 'Аптека не найдена' : undefined} /> : (
        <>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 640 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 32 }}>№</TableCell>
                  {columns.map(c => (
                    <TableCell key={c.key} align={c.align} sortDirection={sort.key === c.key ? (sort.dir === 1 ? 'asc' : 'desc') : false}>
                      <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key && sort.dir === 1 ? 'asc' : 'desc'} onClick={() => toggleSort(c.key)}>
                        {c.key === 'delta' && data.previousMonth ? `К ${monthLabel(data.previousMonth).toLowerCase()}` : c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, shown).map(p => {
                  const d = deltaPct(p.sum, p.prevSum);
                  const isSelected = selected.indexOf(p.guid) !== -1;
                  return (
                    <TableRow key={p.guid} hover selected={isSelected} onClick={() => onPick(p.guid)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ color: '#94a3b8 !important' }}>{rank[p.guid]}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={p.name}>{p.name}</Typography>
                          {!p.active && <Chip size="small" label="нет учётки" sx={{ height: 18, fontSize: '0.64rem', bgcolor: '#f1f5f9', color: '#64748b' }} />}
                        </Box>
                        <SubText>{p.num}{p.region ? ` · ${p.region}` : ''}</SubText>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 150 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmtMoney(p.sum)}</Typography>
                        <ShareBar value={p.share} max={maxShare} label={fmtPct(p.share)} />
                      </TableCell>
                      {withPrev && (
                        <TableCell align="right" sx={{ color: d === null ? undefined : `${d >= 0 ? '#15803d' : '#b91c1c'} !important` }}>
                          {d === null ? (p.prevSum === null || p.prevSum === 0 ? 'новые' : '-') : fmtDelta(d)}
                          <SubText>{p.prevSum ? fmtMoney(p.prevSum) : 'не было записей'}</SubText>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {fmtInt(p.count)}
                        {withPrev && p.prevCount !== null && <SubText>было {fmtInt(p.prevCount)}</SubText>}
                      </TableCell>
                      <TableCell align="right">{fmtInt(p.products)}</TableCell>
                      <TableCell align="right" sx={{ color: p.expiredSum ? `${CHART.expired} !important` : undefined }}>
                        {p.expiredSum ? fmtMoney(p.expiredSum) : '-'}
                        {p.expiredCount > 0 && <SubText>{fmtInt(p.expiredCount)} зап.</SubText>}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.expiredShare >= 10 ? `${CHART.expired} !important` : undefined, fontWeight: p.expiredShare >= 10 ? 600 : 400 }}>
                        {p.expiredSum ? fmtPct(p.expiredShare) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: p.d30Sum ? `${CHART.d30} !important` : undefined }}>
                        {p.d30Sum ? fmtMoney(p.d30Sum) : '-'}
                        {p.d30Count > 0 && <SubText>{fmtInt(p.d30Count)} зап.</SubText>}
                      </TableCell>
                      <TableCell>{fmtDay(p.nearestExpiry)}</TableCell>
                      <TableCell align="right">{p.avgDaysLeft === null ? '-' : fmtDuration(Math.round(p.avgDaysLeft))}</TableCell>
                      <TableCell align="right">{fmtInt(p.activeDays)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStamp(p.lastRecord)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > shown && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShown(s => s + PAGE)} sx={{ textTransform: 'none' }}>
                Показать ещё {Math.min(PAGE, rows.length - shown)} из {fmtInt(rows.length - shown)}
              </Button>
            </Box>
          )}
        </>
      )}
    </ChartCard>
  );
};

const PharmaciesView: React.FC<Props> = ({ filters, paramsKey, active, reloadToken, onPatch }) => {
  const state = useLoad(
    active ? (silent: boolean, signal: AbortSignal) => journalApi.pharmacies(filters, silent, signal) : null,
    paramsKey, reloadToken, 'Не удалось построить рейтинг аптек'
  );
  const data = state.data;
  const selected = filters.pharmacies;

  const stats = useMemo<Stat[]>(() => {
    if (!data) return [];
    const list = data.pharmacies;
    const total = list.reduce((s, p) => s + p.sum, 0);
    const sums = list.map(p => p.sum).sort((a, b) => a - b);
    const mid = Math.floor(sums.length / 2);
    const median = !sums.length ? 0 : sums.length % 2 ? sums[mid] : (sums[mid - 1] + sums[mid]) / 2;
    const top10 = list.slice(0, 10).reduce((s, p) => s + p.sum, 0);
    const withExpired = list.filter(p => p.expiredSum > 0);
    const heavy = list.filter(p => p.expiredShare >= 10).length;
    const inactive = list.filter(p => !p.active).length;
    const out: Stat[] = [
      { label: 'Аптек с записями', value: fmtInt(list.length), hint: inactive ? `без активной учётки: ${inactive}` : undefined },
      { label: 'Сумма', value: fmtMoney(total), hint: `в среднем ${fmtMoney(list.length ? total / list.length : 0)}` },
      { label: 'Медиана по аптеке', value: fmtMoney(median), tooltip: 'Половина аптек вносит меньше этой суммы, половина - больше' },
      { label: 'Доля топ-10 аптек', value: fmtPct(share(top10, total)), hint: fmtMoney(top10) },
      {
        label: 'С просроченным товаром', value: fmtInt(withExpired.length), color: withExpired.length ? CHART.expired : undefined,
        hint: `${fmtPct(share(withExpired.length, list.length))} аптек`
      },
      { label: 'Просрочка 10% и больше', value: fmtInt(heavy), color: heavy ? CHART.expired : undefined, tooltip: 'Аптеки, у которых просроченный товар - не меньше 10% их суммы в журнале' }
    ];
    if (data.previousMonth) {
      const prev = list.reduce((s, p) => s + (p.prevSum || 0), 0);
      const d = deltaPct(total, prev);
      out.push({
        label: `К ${monthLabel(data.previousMonth).toLowerCase()}`, value: d === null ? 'нет данных' : fmtDelta(d),
        color: d === null ? undefined : d >= 0 ? '#15803d' : '#b91c1c', hint: `было ${fmtMoney(prev)}`,
        tooltip: 'Сумма прошлого месяца по тем же аптекам (без аптек, у которых в этом месяце записей нет)'
      });
    }
    return out;
  }, [data]);

  const pick = (guid: string) => onPatch({ pharmacies: selected.length === 1 && selected[0] === guid ? [] : [guid] });

  if (!data) {
    return (
      <ChartCard wide title="Аптеки" loading={state.loading} error={state.error?.text} onRetry={state.retry}>
        {!state.error && <ChartSkeleton height={420} />}
      </ChartCard>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
          {state.error.text}. На экране - данные прежнего запроса.
        </Alert>
      )}
      {selected.length > 0 && (
        <Alert severity="info" icon={false} sx={{ py: 0, fontSize: '0.78rem' }} action={<Button color="inherit" size="small" onClick={() => onPatch({ pharmacies: [] })}>Все аптеки</Button>}>
          Показаны только выбранные аптеки ({selected.length}). Рейтинг строится по ним.
        </Alert>
      )}
      <StatStrip items={stats} />
      <RankChart data={data} onPick={pick} />
      <PharmaciesTable data={data} selected={selected} onPick={pick} />
    </Box>
  );
};

export default React.memo(PharmaciesView);
